import React, { useEffect, useState } from "react";
import { api, imgUrl } from "@/lib/api";
import { X, Play } from "lucide-react";
import { motion } from "framer-motion";

export default function GalleryPage() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => { api.get("/gallery").then(({ data }) => setItems(data)).catch(() => {}); }, []);

  return (
    <div data-testid="gallery-page">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-8">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">Life at the home</p>
        <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tight text-[#1F4D2E] leading-[0.95]">Moments worth protecting.</h1>
        <p className="text-lg text-[#4F6B54] mt-6 max-w-2xl leading-relaxed">Real days, real smiles. Every photo is a child who now has a safe place to grow.</p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        {items.length === 0 ? (
          <p className="text-[#4F6B54] py-16 text-center" data-testid="no-photos">Photos coming soon.</p>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5">
            {items.map((g, i) => (
              <motion.button key={g.id} onClick={() => setActive(g)} data-testid={`gallery-item-${g.id}`}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: (i % 3) * 0.08 }}
                className="mb-5 w-full break-inside-avoid group block overflow-hidden border border-[#1F4D2E]/10 bg-white text-left hover:shadow-[0_16px_36px_rgb(0,0,0,0.08)] transition-shadow">
                <div className="relative overflow-hidden">
                  {g.media_type === "video" ? (
                    <>
                      <video src={imgUrl(g.image_url)} muted playsInline preload="metadata" className="w-full h-auto max-h-[560px] object-cover" />
                      <span className="absolute inset-0 flex items-center justify-center pointer-events-none"><span className="w-14 h-14 rounded-full bg-black/55 text-white flex items-center justify-center"><Play size={24} className="ml-1" /></span></span>
                    </>
                  ) : (
                    <img src={imgUrl(g.image_url)} alt={g.caption || "Refugium Ethiopia"} className="w-full h-auto max-h-[560px] object-cover group-hover:scale-105 transition-transform duration-500" />
                  )}
                </div>
                {g.caption && <p className="text-sm text-[#4F6B54] p-4">{g.caption}</p>}
              </motion.button>
            ))}
          </div>
        )}
      </section>

      {active && (
        <div className="fixed inset-0 z-[70] bg-black/85 flex items-center justify-center p-4" onClick={() => setActive(null)} data-testid="gallery-lightbox">
          <button className="absolute top-5 right-5 text-white/80 hover:text-white" onClick={() => setActive(null)} data-testid="lightbox-close"><X size={30} /></button>
          <figure className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            {active.media_type === "video" ? (
              <video src={imgUrl(active.image_url)} controls autoPlay playsInline className="max-h-[80vh] max-w-full w-auto mx-auto rounded-lg bg-black object-contain" data-testid="gallery-lightbox-video" />
            ) : (
              <img src={imgUrl(active.image_url)} alt={active.caption || ""} className="max-h-[80vh] max-w-full w-auto mx-auto object-contain" />
            )}
            {active.caption && <figcaption className="text-center text-white/80 mt-4 text-sm">{active.caption}</figcaption>}
          </figure>
        </div>
      )}
    </div>
  );
}
